import { useState } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ArcadeButton, Flex, Stack, Text } from '@cash-design-system/react'
import { AllocationDestination } from '@cash-web/protos/squareup/cash/paychecks/common.pb'
import { Color } from '@cash-web/protos/squareup/cash/ui/Color.pb'
import { GiantPercentageInput } from '@cash-web/shared-feature-common'
import { ModalPortal } from '@cash-web/shared-feature-modal'
import { PieChart } from '@cash-web/shared-feature-pie-chart'
import { unicodeToEmojiSvg } from '@cash-web/shared-ui-emojis'
import { replaceWithCDNImageUrl } from '@cash-web/shared-util-url'

import { Screen } from './BlockerMultiAllocation'
import { useBlockerMultiAllocation } from './useBlockerMultiAllocation'

const AllocationButton = styled(ArcadeButton)`
  flex-basis: 30%;
`

const PaycheckDistributionHeroImage = ({ imageHref }: { imageHref?: string }) => {
  return <image href={imageHref} width="12" height="12" x="44.5%" y="24.5%" mask="url(#circle)" />
}

type PaychecksDistributionHeroProps = {
  hero: { color: string; value: string; label: string }
  avatar_url?: string
  iconUnicode?: string
}
const Hero = ({ avatar_url, iconUnicode, hero }: PaychecksDistributionHeroProps) => {
  const { colors } = useTheme()
  const { darkMode } = useTheme()

  return (
    <>
      {avatar_url ? (
        <>
          <defs>
            <mask id="circle">
              <circle cx="50%" cy="30%" r="6" fill="white" />
            </mask>
          </defs>
          <circle cx="50%" cy="30%" r="6" fill={colors.formFieldBackground} />
          <PaycheckDistributionHeroImage imageHref={replaceWithCDNImageUrl({ url: avatar_url })} />
        </>
      ) : iconUnicode ? (
        <PaycheckDistributionHeroImage
          imageHref={unicodeToEmojiSvg({
            unicode: iconUnicode,
            size: 24,
            darkMode,
          })}
        />
      ) : null}
      <text x="50%" y="47%" textAnchor="middle" dominantBaseline="middle" fontWeight="700" fill={colors.label}>
        {hero.value}
      </text>
      <text x="50%" y="57.5%" textAnchor="middle" dominantBaseline="middle" fontSize={5} fill={colors.textSubtle}>
        {hero.label}
      </text>
    </>
  )
}

const PRESET_ALLOCATIONS = [100, 500, 1000, 1500, 2000]

type AllocationEditProps = {
  allocationData: { label: string; allocation_destination: AllocationDestination; color: string; value: number }
  setScreen: (screen: Screen) => void
  tintColors: Color[]
}
export const EditAllocation = ({ setScreen, allocationData, tintColors }: AllocationEditProps) => {
  const { t } = useTranslation()
  const { spacing } = useTheme()
  const [shouldShowManualInput, setShouldShowManualInput] = useState(false)
  const form = useFormContext()

  const {
    allocationBasis,
    avatarUrl,
    iconUnicode,
    confirmCustomAmount,
    formattedValue,
    setFormattedValue,
    handleBasisChange,
    totalAvailableAllocation,
    arcs,
    hero,
  } = useBlockerMultiAllocation({ form, allocationData, tintColors, setShouldShowManualInput })

  let title
  if (allocationData.allocation_destination.investing) {
    title = t('How much do you want to invest?', { context: 'distribution input label' })
  } else if (allocationData.allocation_destination.savings) {
    title = t('How much do you want to save?', { context: 'distribution input label' })
  }

  return (
    <Stack space={spacing.m}>
      <PieChart arcs={arcs} hero={<Hero avatar_url={avatarUrl} iconUnicode={iconUnicode} hero={hero} />} />
      <Flex flexDirection="column" gap={spacing.xl}>
        <Flex flexWrap="wrap" gap={spacing.m} justifyContent="space-between">
          {PRESET_ALLOCATIONS.map(amount => (
            <AllocationButton key={amount} prominence="standard" onClick={() => handleBasisChange(amount)}>
              {`${amount / 100}%`}
            </AllocationButton>
          ))}
          <AllocationButton prominence="standard" onClick={() => setShouldShowManualInput(true)}>
            {'. . .'}
          </AllocationButton>
        </Flex>
        <ArcadeButton size="cta" prominence="prominent" onClick={() => setScreen('home')}>
          {t('Continue', { context: 'cta button' })}
        </ArcadeButton>
      </Flex>
      {shouldShowManualInput && (
        <ModalPortal
          onClose={() => {
            setFormattedValue('')
            setShouldShowManualInput(false)
          }}
        >
          <Stack space={spacing.l}>
            <Text typography="numeralSmall" color="textStandard">
              {title}
            </Text>
            <GiantPercentageInput
              name="distributionAmount"
              setFormattedValue={setFormattedValue}
              minimumLimit={0}
              maximumLimit={totalAvailableAllocation / 100}
              formattedValue={formattedValue}
              value={Number(allocationBasis)}
              isAllowed={({ floatValue }) => (floatValue ?? 0) <= 100}
            />
            <ArcadeButton size="cta" prominence="prominent" onClick={confirmCustomAmount}>
              {t('Next', { context: 'button text' })}
            </ArcadeButton>
          </Stack>
        </ModalPortal>
      )}
    </Stack>
  )
}
